import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './PrivacyPolicy.module.css';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1>Privacy Policy</h1>
        <p className={styles.lastUpdated}>Last updated: July 29, 2025</p>

        <section className={styles.section}>
          <h2>1. Introduction</h2>
          <p>
            Welcome to WealthPlay ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and web service (the "Service"). Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the application.
          </p>
        </section>

        <section className={styles.section}>
          <h2>2. Information We Collect</h2>
          
          <h3>Personal Information</h3>
          <p>When you register for WealthPlay, we may collect:</p>
          <ul>
            <li>Name and email address</li>
            <li>Account credentials (encrypted passwords)</li>
            <li>Profile information you choose to provide</li>
          </ul>

          <h3>Financial Information</h3>
          <p>Through our integration with Plaid API, we may collect:</p>
          <ul>
            <li>Bank account information</li>
            <li>Transaction data</li>
            <li>Account balances</li>
            <li>Financial institution names</li>
          </ul>

          <h3>Usage Information</h3>
          <p>We automatically collect certain information when you use our Service:</p>
          <ul>
            <li>Game scores and progress</li>
            <li>Quest completion data</li>
            <li>App usage patterns</li>
            <li>Device information and identifiers</li>
          </ul>

          <h3>Local Storage Data</h3>
          <p>Our application stores certain information locally on your device to enhance your user experience, including:</p>
          <ul>
            <li>Experience points (XP) and achievement progress</li>
            <li>Quest completion status and timers</li>
            <li>Budget preferences and settings</li>
            <li>Game progress and high scores</li>
          </ul>
          <p>This locally stored data is not automatically backed up to our servers and may be lost if you clear your browser data or uninstall the application.</p>
        </section>

        <section className={styles.section}>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide and maintain our Service</li>
            <li>Process and categorize your financial transactions</li>
            <li>Generate budgeting insights and recommendations</li>
            <li>Track your progress in games and quests</li>
            <li>Send you important account notifications</li>
            <li>Improve our Service and develop new features</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>4. Information Sharing and Disclosure</h2>
          <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
          <ul>
            <li><strong>Service Providers:</strong> We use Plaid to securely connect to your financial accounts</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            <li><strong>With Your Consent:</strong> When you explicitly agree to share information</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes:
          </p>
          <ul>
            <li>Encryption of sensitive data in transit through HTTPS protocols</li>
            <li>Secure authentication using industry-standard JWT tokens and bcrypt password hashing</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>6. Data Storage and Location</h2>
          <p>
            Your data is stored on secure servers located in the United States. By using our Service, you consent to the storage and processing of your information in the United States. We ensure that your data is protected in accordance with this Privacy Policy regardless of where it is stored.
          </p>
        </section>

        <section className={styles.section}>
          <h2>7. Third-Party Services</h2>
          <p>
            Our Service integrates with Plaid to access your financial account information. Plaid's use of your information is governed by their privacy policy, which can be found at <a href="https://plaid.com/legal/#consumers" target="_blank" rel="noopener noreferrer">https://plaid.com/legal/#consumers</a>.
          </p>
        </section>

        <section className={styles.section}>
          <h2>8. Cookies and Tracking Technologies</h2>
          <p>
            WealthPlay does not use cookies or tracking technologies for advertising purposes. We do not use any third-party analytics or advertising services. The application may use essential session data stored locally on your device to maintain your login state and provide core functionality.
          </p>
        </section>

        <section className={styles.section}>
          <h2>9. Advertising</h2>
          <p>
            WealthPlay does not display any advertisements. We do not share your information with advertisers or use your data for advertising purposes. Our Service is supported without the use of ads to ensure your privacy and provide an uninterrupted user experience.
          </p>
        </section>

        <section className={styles.section}>
          <h2>10. Data Retention</h2>
          <p>
            We retain your personal information for as long as your account remains active and as necessary to provide our Service and fulfill the purposes outlined in this Privacy Policy. We may also retain certain information as required by law or for legitimate business purposes.
          </p>
        </section>

        <section className={styles.section}>
          <h2>11. Your Rights and Choices</h2>
          <p>You have the following rights regarding your information:</p>
          <ul>
            <li>Disconnect your bank accounts at any time through the "Disconnect Bank Account" button in your Profile page, through your financial institution, or by contacting Plaid directly</li>
            <li>Contact us to request information about your data or to discuss your privacy concerns</li>
            <li>Request deletion of your account and all associated data by emailing us at devan@wealthplay.com</li>
            <li>Access and download your personal information</li>
          </ul>
          <p>To exercise these rights or for assistance with your account, please contact us using the information provided below.</p>
        </section>

        <section className={styles.section}>
          <h2>12. Children's Privacy</h2>
          <p>
            Our Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
          </p>
        </section>

        <section className={styles.section}>
          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes. Your continued use of the Service after any modifications indicates your acceptance of the updated Privacy Policy.
          </p>
        </section>

        <section className={styles.section}>
          <h2>14. Contact Us</h2>
          <p>If you have any questions about this Privacy Policy, please contact us at:</p>
          <div className={styles.contactInfo}>
            <p>Email: devan@wealthplay.com</p>
            <p>Subject: Privacy Policy Inquiry</p>
          </div>
        </section>

        <div style={{ 
          textAlign: 'center', 
          marginTop: '2rem',
          marginBottom: '2rem',
          paddingTop: '1rem',
          borderTop: '1px solid #e5e7eb'
        }}>
          <p style={{ marginBottom: '20px' }}>
            View our{' '}
            <a href="#/terms-of-service" style={{ color: 'hsl(260, 80%, 74%)', textDecoration: 'underline' }}>
              Terms of Service
            </a>
            {' • '}
            Want to delete your account? Visit our{' '}
            <a href="#/account-deletion" style={{ color: 'hsl(260, 80%, 74%)', textDecoration: 'underline' }}>
              Account Deletion
            </a>{' '}
            page.
          </p>
          <button 
            onClick={handleGoBack}
            style={{
              background: 'hsl(260, 80%, 74%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#8b5cf6'}
            onMouseOut={(e) => e.currentTarget.style.background = 'hsl(260, 80%, 74%)'}
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;